import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  schema?: object;
}

export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogImage,
  schema,
}: SEOProps) => {
  useEffect(() => {
    // Update title
    if (title) {
      document.title = title;
    }

    // Update meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    if (description) {
      updateMetaTag('description', description);
      updateMetaTag('og:description', description, true);
      updateMetaTag('twitter:description', description, true);
    }

    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    if (title) {
      updateMetaTag('og:title', title, true);
      updateMetaTag('twitter:title', title, true);
    }

    if (ogImage) {
      updateMetaTag('og:image', ogImage, true);
      updateMetaTag('twitter:image', ogImage, true);
    }

    // Update canonical
    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonical);
    }

    // Update or add Schema.org JSON-LD
    if (schema) {
      const scriptId = 'schema-org-jsonld';
      let script = document.getElementById(scriptId);
      
      if (!script) {
        script = document.createElement('script');
        script.id = scriptId;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      
      script.textContent = JSON.stringify(schema);
    }

    // Cleanup function
    return () => {
      const script = document.getElementById('schema-org-jsonld');
      if (script) {
        script.remove();
      }
    };
  }, [title, description, keywords, canonical, ogImage, schema]);
};

// Generate Organization Schema
export const generateOrganizationSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://runxing.cn';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '润星集团',
    alternateName: ['润星', 'Runxing'],
    url: siteUrl,
    logo: 'https://www.mizean.com/zb_users/upload/2025/12/Rstar%E6%B6%A6%E6%98%9F%E9%9B%86%E5%9B%A2logo.jpg',
    description: '润星集团是立足内容营销领域，以明星、艺人及网红主播为核心驱动力的综合性营销服务集团。年营收超500亿，服务1000+品牌，业务覆盖电商直播、短视频切片、短剧、自有品牌、明星经纪、互动营销、数字供应链、产业链投资等多维业务单元。',
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'CN',
      addressRegion: '中国',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'info@we3.vip',
      contactType: '商务咨询',
    },
    sameAs: [
      siteUrl,
    ],
  };
};

// Generate WebSite Schema
export const generateWebSiteSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://runxing.cn';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '润星集团官网',
    alternateName: '润星官网',
    url: siteUrl,
    description: '润星集团是立足内容营销领域，以明星、艺人及网红主播为核心驱动力的综合性营销服务集团。业务覆盖电商直播、短视频切片、短剧、自有品牌、明星经纪、互动营销、数字供应链、产业链投资等多维业务单元。',
    publisher: {
      '@type': 'Organization',
      name: '润星集团',
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: `${siteUrl}/?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };
};

// Generate Service Schema
export const generateServiceSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://runxing.cn';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: '内容营销服务',
    provider: {
      '@type': 'Organization',
      name: '润星集团',
      url: siteUrl,
    },
    areaServed: {
      '@type': 'Country',
      name: '中国',
    },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: '营销服务',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: '电商直播',
            description: '打造现象级直播间，涵盖达人混播与品牌店播，以超高GMV转化率为核心指标。',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: '短视频切片',
            description: '专业短视频内容制作与分发，助力品牌在短视频平台实现内容营销。',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: '短剧制作',
            description: '创意短剧内容制作，结合品牌营销需求，打造爆款短剧内容。',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: '明星经纪',
            description: '独家签约一线艺人与潜力新星，精准匹配品牌调性，实现声量与形象的双重提升。',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: '互动营销',
            description: '创意驱动的社交媒体营销，制造全网热搜话题，深度连接 Z 世代消费者。',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: '数字供应链',
            description: 'AI 赋能选品与销售管理，链接全球优质货源，为直播电商提供强大的后端支持。',
          },
        },
      ],
    },
  };
};

// Generate BreadcrumbList Schema
export const generateBreadcrumbSchema = (items: Array<{ name: string; url: string }>) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://runxing.cn';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${siteUrl}${item.url}`,
    })),
  };
};
